import { inject, Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { from, of, throwError } from 'rxjs';
import { catchError, map, mapTo, switchMap, tap } from 'rxjs/operators';
import * as authActions from './auth.actions';
import * as userActions from '../user/user.actions';
import { AuthService } from '../../services/auth.service';
import { EmptyAction } from '..';
import { SnackBarService } from '../../services/snackbar.service';
import { UserService } from '../../services/user.service';
import { Store } from '@ngrx/store';
import { ITempToken } from '../../models/authentication';
import { SessionService } from '../../services/session.service';
import { Router } from '@angular/router';
import { CorePaths } from '../../modules/core/core-routing.module';
import { SubscriptionTabs } from '../../modules/core/subscription/subscription.component';
import { ISubscriptionInfo, SubscriptionType } from '../../models/Subscription';
import { BookPermissionsService } from '../../services/book-permissions.service';

@Injectable()
export class AuthEffects {
  // Inject Services and Actions
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private snackBarService = inject(SnackBarService);
  private userService = inject(UserService);
  private store = inject(Store);
  private sessionService = inject(SessionService);
  private router = inject(Router);
  private bookPermissions = inject(BookPermissionsService);

  private toShowSubscriptionPageOnLogin(): boolean {
    const tokenInfo = this.authService.getDecodedToken();
    const primaryId = tokenInfo?.['UserInfo']?.PrimarySubscription;
    const subscriptions: ISubscriptionInfo[] =
      tokenInfo?.['UserInfo']?.Subscriptions ?? [];
    const sub = subscriptions.find((s) => s.Id === primaryId);

    if (!sub) return false;

    const now = new Date();

    // When the Subscription Type Is Free
    if (sub.SubscriptionType === SubscriptionType.Free) {
      const oneMonthLater = new Date(sub.StartDate);
      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
      return now >= oneMonthLater;
    }
    // When the Subscription Type Is Not Free
    return now > new Date(sub.EndDate); // If the Plan Has Expired
  }

  // Fetch Client Info
  fetchClientInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.FetchClientInfoAction),
      switchMap((props) => {
        return this.authService.getClientInfo().pipe(
          map((res) => {
            return authActions.FetchClientInfoSuccessAction({
              clientInfo: res.Value,
            });
          }),
          catchError((err) => {
            return of(
              authActions.FetchClientInfoFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Fetch Client Info Failure
  fetchClientInfoFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.FetchClientInfoFailureAction),
      tap(({ error }) => {
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction()),
    ),
  );

  // Fetch Temp Token
  fetchTempTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.FetchTempTokenAction),
      switchMap((props) => {
        return this.authService
          .getTempToken(
            props.request.AuthCode,
            props.request.RedirectUri,
            props.provider,
            props.request.AppleUser,
          )
          .pipe(
            map((res) => {
              return authActions.FetchTempTokenSuccessAction({ response: res });
            }),
            catchError((err) => {
              return of(
                authActions.FetchTempTokenFailureAction({
                  error: err.error,
                }),
              );
            }),
          );
      }),
    ),
  );

  // Fetch Temp Token Success
  fetchTempTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.FetchTempTokenSuccessAction),
      switchMap((props) => {
        this.authService.saveTempTokenInfo(props.response.Value);
        this.store.dispatch(userActions.AuthorizeUserAction());
        return of(EmptyAction());
      }),
    ),
  );

  // Fetch Temp Token Failure
  fetchTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.FetchTempTokenFailureAction),
      switchMap((props) => {
        return of(EmptyAction());
      }),
    ),
  );

  // Login Successful
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.LoginSuccessFulAction),
      switchMap((props) => {
        this.userService.saveDecodedTokenInfo(
          props.loginSuccessInfo.AccessToken,
        );
        this.authService.onSuccessFulLogin(props.loginSuccessInfo);
        // Initialize the Session Service
        return from(this.sessionService.initializeApp()).pipe(
          switchMap(() => {
            // Dispatch GetUserAction after successful session initialization
            this.store.dispatch(
              userActions.GetUserAction({
                request: {
                  routeParameters: {
                    userId: this.userService.getDecodedTokenInfo()?.UserId!,
                  },
                  queryParameters: {
                    includeRoles: true,
                  },
                },
              }),
            );

            // Hide any existing SnackBar
            this.snackBarService.closeSnackBar();
            if (
              this.authService.isAutoLoggedIn() &&
              this.toShowSubscriptionPageOnLogin() &&
              !this.router.url.startsWith(
                `/${CorePaths.subscription}/create`,
              ) &&
              !this.router.url.startsWith('/' + CorePaths.profile)
            ) {
              // When User Logins Using SSO or OTP
              this.router.navigate([CorePaths.subscription], {
                queryParams: { tab: SubscriptionTabs.PLANS },
                state: {
                  showSubscriptionAsDialog: true,
                },
              });
            }
            return of(EmptyAction());
          }),
          catchError((error) => {
            return of(EmptyAction());
          }),
        );
      }),
    ),
  );

  // Generate OTP
  generateOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.GenerateOTPAction),
      switchMap((props) => {
        return this.authService.requestForOTP(props.request).pipe(
          map((res) => {
            this.authService.setOTPGenerateSuccessInfo(res.Value);
            res.Value.Receiver = props.request.Receiver;
            res.Value.DeliveryMode = props.request.DeliveryMode;
            return authActions.GenerateOTPSuccessAction({
              response: res,
            });
          }),
          catchError((err) => {
            return of(
              authActions.GenerateOTPFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Generate OTP Success
  generateOTPSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.GenerateOTPSuccessAction),
      switchMap((props) => {
        this.snackBarService.showSnackBar('success', 'OTP Sent');
        return of(EmptyAction());
      }),
    ),
  );

  generateOTPFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.GenerateOTPFailureAction),
      tap(({ error }) => {
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction()),
    ),
  );

  // Verify OTP
  verifyOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.VerifyOTPAction),
      switchMap((props) => {
        return this.authService.verifyOtp(props.request).pipe(
          map((res) => {
            res.Receiver = props.request.Receiver;
            return authActions.VerifyOTPSuccessAction({ response: res });
          }),
          catchError((err) => {
            return of(
              authActions.VerifyOTPFailureAction({
                error: err.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  // Verify OTP Success
  verifyOTPSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.VerifyOTPSuccessAction),
      switchMap((props) => {
        const tokenInfo: ITempToken = {
          Token: props.response.Value.Token,
          ExpiresIn: props.response.Value.ExpiresIn,
        };
        this.authService.saveTempTokenInfo(tokenInfo);
        // Exclude the "Register" And "Profile" page, will handle OTP Verification
        // Explicitly in the Register And Profile Components respectively
        if (
          !this.router.url.startsWith('/register') &&
          !this.router.url.startsWith('/profile')
        ) {
          this.store.dispatch(userActions.AuthorizeUserAction());
        }
        return of(EmptyAction());
      }),
    ),
  );

  // Verify OTP Failure
  verifyOTPFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.VerifyOTPFailureAction),
      tap(({ error }) => {
        this.snackBarService.showSnackBar('error', error.Errors[0].Message);
      }),
      mapTo(EmptyAction()),
    ),
  );

  // Logout
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.LogoutAction),
      switchMap((props) => {
        if (!props.skipSessionLogout) {
          this.store.dispatch(userActions.SessionLogoutAction());
        }
        this.authService.logout();
        this.store.dispatch(userActions.ClearUserStateAction());
        // The book grants are cached for the session, so they have to
        // be dropped here as well - otherwise the next person to log
        // in on this browser starts out holding the previous user's
        // per-book permissions.
        this.bookPermissions.reset();
        return of(EmptyAction());
      }),
    ),
  );

  // Verify Recaptcha
  verifyRecaptcha$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.VerifyRecaptchaTokenAction),
      switchMap((props) => {
        return this.authService
          .verifyRecaptcha(props.request.RecaptchaUserToken)
          .pipe(
            map((res) => {
              if (res.Value.Allow === true) {
                return authActions.VerifyRecaptchaTokenSuccessAction({
                  response: res,
                });
              } else {
                return authActions.VerifyRecaptchaTokenFailureAction({
                  error: {
                    Errors: [],
                  },
                });
              }
            }),
            catchError((err) => {
              return of(
                authActions.VerifyRecaptchaTokenFailureAction({
                  error: err.message || 'reCAPTCHA verification error',
                }),
              );
            }),
          );
      }),
    ),
  );

  // Verify Recaptcha Failure
  verifyRecaptchaFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.VerifyRecaptchaTokenFailureAction),
      tap(({ error }) => {
        this.snackBarService.showSnackBar(
          'error',
          'Recaptcha Verification Failed',
        );
      }),
      mapTo(EmptyAction()),
    ),
  );
}
